/**
 * Security sanitization utilities for analytics
 * Removes sensitive authentication data from tracking payloads
 * Note: No patient data exists in this system - for dental professional scheduling only
 */

/**
 * List of keys that contain sensitive security information
 */
const SENSITIVE_KEYS = [
  // Authentication information only
  'password',
  'token',
  'apiKey',
  'api_key',
  'secret',
  'refreshToken',
  'refresh_token',
  'accessToken',
  'access_token',
  'authToken',
  'auth_token',
  'bearer',
  'authorization',
  'sessionId',
  'session_id',
  'cookie',
  'csrf',
];

/**
 * Check if a key contains sensitive security information
 */
export const isSensitiveKey = (key: string): boolean => {
  if (!key) return false;

  const keyLower = key.toLowerCase();
  return SENSITIVE_KEYS.some(sensitiveKey => keyLower.includes(sensitiveKey.toLowerCase()));
};

/**
 * Simple payload sanitization - only removes security credentials
 * Preserves all user and business data since no patient information exists
 */
export const sanitizePayload = (payload: any): any => {
  if (!payload) return payload;

  // Primitive types pass through unchanged
  if (typeof payload !== 'object') {
    return payload;
  }

  // Handle arrays
  if (Array.isArray(payload)) {
    return payload.map(item => sanitizePayload(item));
  }

  // Handle objects
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(payload)) {
    // Skip only security-sensitive keys
    if (isSensitiveKey(key)) {
      continue;
    }

    // Recursively handle nested objects
    if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizePayload(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Sanitize URL - only removes auth tokens from query params
 */
export const sanitizeUrl = (url: string): string => {
  if (!url) return url;

  try {
    const urlObj = new URL(url, window.location.origin);
    const params = new URLSearchParams(urlObj.search);
    const sanitizedParams = new URLSearchParams();

    // Remove only auth-related query parameters
    for (const [key, value] of params.entries()) {
      if (!isSensitiveKey(key)) {
        sanitizedParams.set(key, value);
      }
    }

    urlObj.search = sanitizedParams.toString();
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // If URL parsing fails, return the original URL without query params
    const questionIndex = url.indexOf('?');
    return questionIndex > -1 ? url.substring(0, questionIndex) : url;
  }
};